import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ActiveCall, CallUpdates } from 'app/models/dto/call.model';
import { Borrower } from 'app/models/dto/borrower.model';
import { Alert } from 'app/models/dto/alert.model';
import { Message } from 'app/messages/message-service/message.model';
import { MessageState } from 'app/messages/message-store/message.state';
import { MessageAlertDismissed, SendMessage, ThreadReply } from 'app/messages/message-store/message.actions';
import { UpdateCall } from 'app/store/call/call.actions';
import { DispositionState } from 'app/store/disposition/disposition.state';
import { LoadDispositions } from 'app/store/disposition/disposition.actions';
import { DispositionOption } from 'app/models/disposition/disposition.model';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { EventBusService } from 'app/modules/shared/services/event-bus.service';
import { AlertModule } from 'app/components/alert/alert.module';
import { MessagesModule } from 'app/messages/messages.module';
import { NotesModule } from 'app/notes/notes.module';
import { ScheduleReminderModule } from 'app/schedule-reminder/schedule-reminder.module';
import { SmsModule } from 'app/sms/sms.module';

// team.type values the mid-call action is available to; excludes 'customer_service'
// (Slice 14 handles CS via CxInteraction, not a disposition dropdown). Non-AE/ADR
// sales-adjacent teams map to the fallback disposition list on the server, so
// the panel still shows a meaningful list for them.
const CS_TEAM_TYPE = 'customer_service';

// Calls at/over this duration are treated as in-contact; the dropdown drops
// no-contact options so the rep can only submit a Made Contact tag. Mirrors
// App\Models\Call::DURATION_CONTACT_FILTER_SECONDS on the API side.
const DURATION_CONTACT_FILTER_MS = 120_000;

@Component({
  selector: 'app-mid-call-disposition-action',
  templateUrl: './mid-call-disposition-action.component.html',
  styleUrls: ['./mid-call-disposition-action.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatSelectModule,
    AlertModule,
    MessagesModule,
    NotesModule,
    ScheduleReminderModule,
    SmsModule,
  ],
})
export class MidCallDispositionActionComponent implements OnInit, OnChanges {
  @Input() activeCall: ActiveCall | null = null;
  @Input() callDurationMs = 0;
  @Input() teamType: string | null = null;
  // Driven by the smart component's callId-anchored `midCallDispositionCompleted`
  // flag (the same flag that suppresses the forced post-call wrap-up modal).
  // Once a disposition has been saved for this call, the action stays hidden
  // for the rest of the call.
  @Input() midCallDispositionSaved = false;
  @Output() dispositionCompleted = new EventEmitter<void>();

  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private eventBusService = inject(EventBusService);

  dispositions$: Observable<DispositionOption[]> = this.store.select(DispositionState.dispositions);
  alert$: Observable<Alert | null> = this.store.select(MessageState.alert);
  selectedOption: DispositionOption | null = null;

  // "What's next?" sub-flow state. wrapUpOption toggles the panel body between
  // the outcome picker and the selected follow-up action (reminder/note/sms/email).
  wrapUpOption: string | null = null;
  allowCloseWrapUpOption = true;
  reminderSet = false;
  noteLeft = false;
  smsSent = false;
  emailSent = false;

  private allOptions: DispositionOption[] = [];

  ngOnInit(): void {
    if (this.store.selectSnapshot(DispositionState.dispositions).length === 0) {
      this.store.dispatch(new LoadDispositions());
    }
    this.dispositions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => {
        this.allOptions = options;
      });

    // Email create closes itself via the event bus (matches wrap-up-dialog-v2);
    // the short delay lets the send complete before collapsing the sub-flow.
    this.eventBusService.subscribe(this.eventBusService.types.CloseSendEmailViewEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => setTimeout(() => this.closeWrapUpOption(), 500));
  }

  // Getter so the visible set re-evaluates each change-detection cycle — the
  // live call keeps ticking, and once it crosses the 120s threshold we need
  // the no-contact rows to drop out of the dropdown mid-view.
  get visibleOptions(): DispositionOption[] {
    if (this.noContactFiltered) {
      return this.allOptions.filter((option) => option.tag !== null);
    }
    return this.allOptions;
  }

  private get noContactFiltered(): boolean {
    return this.callDurationMs >= DURATION_CONTACT_FILTER_MS;
  }

  ngOnChanges(): void {
    if (this.selectedOption && this.selectedOption.tag === null && this.noContactFiltered) {
      this.selectedOption = null;
    }
  }

  showAction(): boolean {
    if (this.midCallDispositionSaved) {
      return false;
    }
    if (!this.activeCall?.inProgress) {
      return false;
    }
    return this.teamType !== CS_TEAM_TYPE;
  }

  handleSelection(option: DispositionOption): void {
    this.selectedOption = option;
  }

  // Narrowing getters for the sub-flow child inputs. The "What's next?" block
  // only renders once nextStepBtnDisplay() has confirmed the relevant borrower
  // data exists, so the non-null assertions here are safe at render time.
  get borrowerId(): number {
    return (this.activeCall?.borrower?.id ?? this.activeCall?.borrowerId) as number;
  }

  get borrower(): Borrower {
    return this.activeCall?.borrower as Borrower;
  }

  // messages-create only prefills the To: field (from borrower.primaryContact.email)
  // when BOTH borrower and user are truthy. Fall back to the logged-in rep's name
  // so `user` is never empty — otherwise the borrower's email never populates.
  get borrowerUserName(): string {
    return this.activeCall?.borrower?.lenderUser?.value?.fullName
      || this.store.selectSnapshot(LenderUserState.lenderUser)?.fullName
      || '';
  }

  // "What's next?" only makes sense once an outcome is chosen and we have a
  // borrower to act on. needBorrower distinguishes actions that require the
  // full borrower object (sms/email) from those that need only its id
  // (reminder/note).
  nextStepBtnDisplay(needBorrower: boolean): boolean {
    if (!this.activeCall || !this.selectedOption) {
      return false;
    }
    return (needBorrower && !!this.activeCall.borrower)
      || (!needBorrower && !!this.activeCall.borrowerId);
  }

  closeWrapUpOption(): void {
    switch (this.wrapUpOption) {
      case 'sms': this.smsSent = true; break;
      case 'email': this.emailSent = true; break;
      case 'notes': this.noteLeft = true; break;
      case 'reminder': this.reminderSet = true; break;
    }
    this.wrapUpOption = null;
  }

  handleEmailCreate(message: Message): void {
    if (message.threadId) {
      this.store.dispatch(new ThreadReply(message.threadId, message));
    } else {
      this.store.dispatch(new SendMessage(message));
    }
  }

  handleAlertDismissed(): void {
    this.store.dispatch(new MessageAlertDismissed());
  }

  saveDisposition(): void {
    if (!this.selectedOption || !this.activeCall) {
      return;
    }

    if (this.noContactFiltered && this.selectedOption.tag === null) {
      this.selectedOption = null;
      return;
    }
    // Emit legacy disposition string verbatim — the API's validator compares
    // against Disposition::*->value. Auto-infer the disposition for tagged
    // options; no callTag on no-contact rows.
    const updates: CallUpdates = { disposition: this.selectedOption.disposition };
    if (this.selectedOption.tag !== null) {
      updates.callTag = this.selectedOption.tag;
    }
    this.store.dispatch(new UpdateCall(updates));
    // Deliberately does NOT end the call — the rep is still on it. The post-call
    // forced modal is suppressed by call-display-smart via dispositionCompleted.
    this.dispositionCompleted.emit();
  }
}
